import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Image as ImageIcon,
  RefreshCw,
  Download,
  ArrowRight,
  Star,
  Users,
  Clock,
  FileImage
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ImageToolsPage = () => {
  const imageToolCategories = [
    {
      id: 'format-conversion',
      name: 'Format Conversion',
      description: 'Convert images between different formats',
      icon: RefreshCw,
      color: 'from-green-500 to-emerald-500',
      badge: 'Popular',
      badgeVariant: 'default',
      tools: [
        {
          name: 'Convert Image',
          description: 'Convert between JPG, PNG, WebP, BMP, GIF, TIFF',
          path: '/convert-image',
          icon: ImageIcon,
          usage: '7.9k uses'
        }
      ]
    },
    {
      id: 'image-editing',
      name: 'Image Processing',
      description: 'Resize and compress your images',
      icon: FileImage,
      color: 'from-purple-500 to-indigo-500',
      badge: 'Essential',
      badgeVariant: 'secondary',
      tools: [
        {
          name: 'Resize Image',
          description: 'Change image dimensions',
          path: '/resize-image',
          icon: RefreshCw,
          usage: '2.1k uses'
        },
        {
          name: 'Compress Image',
          description: 'Reduce image file size',
          path: '/compress-image',
          icon: Download,
          usage: '2.7k uses'
        }
      ]
    }
  ];

  const stats = [
    { label: 'Image Tools', value: '5', icon: Star },
    { label: 'Images Processed', value: '28.5k', icon: FileImage },
    { label: 'Active Users', value: '2.8k', icon: Users },
    { label: 'Avg Process Time', value: '< 15s', icon: Clock }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-gray-900">Image Tools</h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Professional image conversion and processing tools. Convert formats, resize, and compress your images with ease.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <Card key={stat.label}>
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <IconComponent className="h-5 w-5 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tool Categories */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Available Tools</h2>
        
        {imageToolCategories.map((category) => {
          const CategoryIcon = category.icon;
          return (
            <Card key={category.id} className="overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`bg-gradient-to-r ${category.color} p-3 rounded-lg text-white`}>
                      <CategoryIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <CardTitle className="flex items-center space-x-2">
                        <span>{category.name}</span>
                        {category.badge && (
                          <Badge variant={category.badgeVariant}>{category.badge}</Badge>
                        )}
                      </CardTitle>
                      <p className="text-gray-600 mt-1">{category.description}</p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {category.tools.map((tool) => {
                    const ToolIcon = tool.icon;
                    return (
                      <Link key={tool.path} to={tool.path}>
                        <Card className="hover:shadow-md transition-all duration-200 cursor-pointer group border-gray-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <ToolIcon className="h-5 w-5 text-gray-600 group-hover:text-green-600 transition-colors" />
                                <div>
                                  <h4 className="font-medium text-gray-900 group-hover:text-green-600 transition-colors">
                                    {tool.name}
                                  </h4>
                                  <p className="text-sm text-gray-600">{tool.description}</p>
                                  <p className="text-xs text-gray-500 mt-1">{tool.usage}</p>
                                </div>
                              </div>
                              <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-green-600 transition-colors" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Coming Soon */}
      <Card className="border-dashed border-gray-300">
        <CardContent className="p-8 text-center">
          <div className="space-y-4">
            <div className="text-gray-400">
              <ImageIcon className="h-12 w-12 mx-auto" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">More Image Tools Coming Soon</h3>
              <p className="text-gray-600">
                We're adding more advanced image processing tools including filters, 
                effects, background removal, and batch processing capabilities.
              </p>
            </div>
            <Badge variant="outline">Coming Soon</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImageToolsPage;